import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useTranslation, Trans } from 'react-i18next';
import API from '../../../api/api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatLargeNumber } from '../../../helper';
import {
  Typography,
  Grid,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Tabs,
  Tab,
  Box,
  Toolbar,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Checkbox,
  TextField
} from '@mui/material';
import SaveAltOutlinedIcon from '@mui/icons-material/SaveAltOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import { getSettingsDefault, getSettings, saveSettings, getDeeplSettings, getDeeplSettingsDefault, saveDeeplSettings, getTranslationInstructions, resetDeeplSettings, getBulkSettings, getBulkSettingsDefault, saveBulkSettings, hasAiTranslationFeature, hasBulkTranslationAccess } from '../../../modules/config';
import IOSSwitch from '../../../components/IOSSwitch';
import { UpgradePlanHint } from '../../../components/PricingPlans';
import SolutionsArticleLink from '../../../components/SolutionsArticleLink';

// Looks like a langify source field (outlined, label on the line); disabled because staff own these texts,
// with the text kept in full colour instead of the greyed-out disabled look.
const ReadOnlyHint = ({ label, value, max }) => (
  <TextField
    label={label}
    value={value}
    helperText={max ? `${value.length} / ${max}` : undefined}
    disabled
    multiline
    fullWidth
    variant="outlined"
    InputLabelProps={{ shrink: true }}
    sx={{
      '& .MuiOutlinedInput-root': { padding: '3px 5px' },
      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'unset', color: 'text.primary' },
      '& .MuiInputLabel-root.Mui-disabled': { color: 'text.secondary' },
      '& .MuiFormHelperText-root': { textAlign: 'right', marginRight: 0 },
    }}
  />
);

const styles = {
  warningIcon: {
    color: '#f57c00',
    fontSize: '1.2rem',
    marginLeft: '4px',
  },
  paper: {
    p: 3,
    mb: 2,
  },
  paperFlat: {
    p: 3,
  },
  divider: {
    margin: '1rem 0',
  },
  list: {
    width: '100%',
  },
  listItem: {
    pl: 1,
    pr: 6,
    borderRadius: 1,
    opacity: 0.75,
    transition: 'opacity 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      opacity: 1,
      backgroundColor: 'action.hover',
    },
  },
  listItemWithReset: {
    pl: 1,
    pr: 22,
    borderRadius: 1,
    opacity: 0.75,
    transition: 'opacity 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      opacity: 1,
      backgroundColor: 'action.hover',
    },
  },
  layout: {
    display: 'flex',
    gap: 3,
    alignItems: 'flex-start',
  },
  /**
   * Die Fläche war auf `rgba(235, 235, 235, 1)` festgenagelt, während die Tabs
   * darauf `text.primary` nutzen — im Dark Mode 1.17:1. `surfaceRecessed` ist
   * das Token für eine bewusst zurückgesetzte Fläche und folgt dem Modus.
   * Im Light-Modus wird die Sidebar dadurch etwas heller (#fafafa statt 235).
   */
  tabsSidebar: (theme) => ({
    width: 180,
    flexShrink: 0,
    position: 'sticky',
    top: 64,
    backgroundColor: 'surfaceRecessed',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  }),
  tabsRoot: {
    borderRight: 'none',
  },
  tab: {
    alignItems: 'center',
    textAlign: 'left',
    minWidth: 0,
    minHeight: 'auto',
    textTransform: 'capitalize',
    px: 2,
    fontSize: '0.875rem',
    fontWeight: 400,
    opacity: 1,
    color: 'text.primary',
    // Schwarze Alpha-Tints sind auf dunklem Grund unsichtbar. `action.*` folgt
    // dem Modus — dieselbe Datei macht es in `listItem`/`listItemWithReset`
    // schon so.
    '&.Mui-selected': {
      backgroundColor: 'action.selected',
      fontWeight: 500,
    },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
  },
  infoColumn: (theme) => ({
    width: 260,
    flexShrink: 0,
    alignSelf: 'stretch',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  }),
  infoBox: {
    p: 2,
  },
  namespaceSection: {
    scrollMarginTop: '60px',
  },
  namespaceTitle: {
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  actionBar: {
    position: 'fixed',
    display: 'flex',
    justifyContent: 'space-between',
    zIndex: 2000,
    top: 0,
    left: 0,
    right: 0,
    padding: '0 40px 0 calc(72px + 24px)',
    minHeight: 64,
    '@media (max-width: 600px)': {
      padding: '0 8px 0 8px',
      minHeight: 56,
    },
  },
  // Local copy of the ItemsList save bar; same token so both bars look alike
  highlight: (theme) => ({
    color: theme.palette.saveBar.text,
    backgroundColor: theme.palette.saveBar.background,
  }),
  actions: {
    color: 'text.secondary',
    position: 'relative',
    mr: 1,
    display: 'flex',
    alignItems: 'center',
  },
  appBarLogo: {
    flexShrink: 0,
    marginRight: 2,
    '@media (max-width: 600px)': {
      display: 'none',
    },
  },
  aiNotice: {
    mt: 2,
    p: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'rgba(245, 124, 0, 0.4)',
    backgroundColor: 'rgba(245, 124, 0, 0.06)',
  },
  aiNoticeList: {
    m: 0,
    mb: 1,
    pl: 2.5,
  },
  // secondary text on the orange tint reads too light in light mode; dark mode keeps the token
  aiNoticeText: (theme) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.text.primary,
  }),
  aiBetaNotice: {
    mt: 1,
    p: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'action.hover',
  },
};

const AI_TRANSLATION_SOLUTION_URL = 'https://support.langify-app.com/support/solutions/articles/11000137671-ai-translation-for-bulk-translations';

// Contact for shops that want to join the closed AI translation beta.
const AI_BETA_CONTACT_MAIL = 'support@langify-app.com';

// After the beta, AI translation above this many products needs the Premium plan; a Growth
// store above it is told so upfront
const AI_PRODUCT_LIMIT = 10000;

// Short list of AI-translation weaknesses shown when AI bulk translation is selected.
// The long version lives in the solution article linked above.
// Scroll to the AI consent checkbox: how long it takes and how much room stays below it.
const AI_CONSENT_SCROLL_DURATION = 1200;
const AI_CONSENT_SCROLL_MARGIN = 24;

const AI_WEAKNESS_KEYS = [
  { key: 'point1', text: 'Names, numbers and units are sometimes translated or reformatted.' },
  { key: 'point2', text: 'Very short texts are the most error-prone, and in rare cases a sentence is left out.' },
  { key: 'point3', text: 'Legal texts and safety notices always need a review.' },
];

function BulkConfiguration() {
  // styles defined above as plain object for sx props
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Für das Logo auf der Save-Bar: ein <img src> braucht den Modus in JS,
  // per sx ist die Asset-Wahl nicht abbildbar.
  const isDark = useTheme().palette.mode === 'dark';

  const configData = useSelector(state => state.config.settings_data);
  const defaultConfigData = useSelector(state => state.config.default_settings_data);
  const isDefaultPending = useSelector(state => state.config.get_settings_default_pending);
  const isSavePending = useSelector(state => state.config.save_settings_pending);
  const error = useSelector(state => state.config.error);
  const deeplSettingsData = useSelector(state => state.config.deepl_settings_data);
  const deeplSettingsDefaultData = useSelector(state => state.config.deepl_settings_default_data);
  const isSaveDeeplPending = useSelector(state => state.config.save_deepl_settings_pending);
  const translationInstructions = useSelector(state => state.config.translation_instructions);
  const languages = useSelector(state => state.languages.languages);
  const bulkSettingsData = useSelector(state => state.config.bulk_settings_data);
  const bulkSettingsDefaultData = useSelector(state => state.config.bulk_settings_default_data);
  const isSaveBulkPending = useSelector(state => state.config.save_bulk_settings_pending);
  const chargeName = useSelector(state => state.shop.shop.charge?.name);
  const langifyFeatures = useSelector(state => state.shop.shop.langify_features);
  // the bulk feature opens the whole tool below Growth, the settings included
  const isPlanRestricted = !hasBulkTranslationAccess(chargeName, langifyFeatures);
  // AI translation is in a closed beta: staff grants it per shop, so without the feature the
  // option stays walled off. The backend enforces this too — the UI gate is convenience only.
  const isAiBetaLocked = !hasAiTranslationFeature(langifyFeatures);
  // Below Growth the whole section already sits behind the plan wall, so the beta wall would be
  // a second grey layer on top and the notice underneath it unreachable. The plan hint speaks
  // first there; the beta wall only shows where the section is otherwise usable.
  const showAiBetaWall = isAiBetaLocked && !isPlanRestricted;

  // Growth stores learn about the coming product limit; the count is one cheap request
  const [productsTotal, setProductsTotal] = useState(null);
  const [productsTotalPending, setProductsTotalPending] = useState(false);
  useEffect(() => {
    if(chargeName !== 'growth') return;
    let stale = false;
    setProductsTotalPending(true);
    API.getSectionCounts(['products'], (res) => {
      if(stale) return;
      if(res && res.success && typeof res.counts?.products === 'number') setProductsTotal(res.counts.products);
      setProductsTotalPending(false);
    });
    return () => { stale = true; };
  }, [chargeName]);
  const aiProductLimitRef = useRef(null);

  const [modifiedConfigData, setModifiedConfigData] = useState([]);
  const [originalConfigData, setOriginalConfigData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [infoTop, setInfoTop] = useState(70);
  const [hoveredField, setHoveredField] = useState(null);
  const [triangleTop, setTriangleTop] = useState(0);
  const infoColumnRef = useRef(null);
  const infoBoxRef = useRef(null);
  const sectionRefs = useRef({});
  const namespacesRef = useRef([]);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const configInitializedRef = useRef(false);
  const justSavedRef = useRef(false);
  const hoveredFieldRef = useRef(null);
  const hoverCenterYRef = useRef(0);
  const [deeplFormality, setDeeplFormality] = useState('default');
  const [originalDeeplFormality, setOriginalDeeplFormality] = useState('default');
  const deeplJustSavedRef = useRef(false);
  const [bulkType, setBulkType] = useState('default');
  // only once AI translation is the chosen bulk type
  const showAiProductLimitNotice = bulkType === 'ai' && chargeName === 'growth' && productsTotal !== null && productsTotal > AI_PRODUCT_LIMIT;
  const [originalBulkType, setOriginalBulkType] = useState('default');
  // AI translation may only be saved with an explicit GDPR/quality confirmation.
  // Already-stored 'ai' counts as previously confirmed; leaving 'ai' clears it again,
  // so coming back to AI always requires a fresh confirmation.
  const [aiConsent, setAiConsent] = useState(false);
  // The notice with the consent box shows while AI is being enabled. As long as AI is the stored
  // setting the confirmation has been given, however often the radio is toggled meanwhile
  const bulkJustSavedRef = useRef(false);
  const aiConsentRef = useRef(null);
  // Set by the radio handler only, so the consent box is scrolled to when the user picks AI,
  // not when the stored setting happens to be AI on load.
  const scrollToAiConsentRef = useRef(false);

  useEffect(() => {
    dispatch(getSettingsDefault());
    dispatch(getSettings());
    dispatch(getDeeplSettingsDefault());
    dispatch(getDeeplSettings());
    dispatch(getTranslationInstructions());
    dispatch(getBulkSettingsDefault());
    dispatch(getBulkSettings());
  }, [dispatch]);

  useEffect(() => {
    if (bulkJustSavedRef.current) {
      bulkJustSavedRef.current = false;
      return;
    }
    // A stored "ai" is ignored while the beta feature is missing — a shop that was taken out of
    // the beta shows (and saves) the default engine, matching what the backend actually runs.
    const effective = (value) => (value === 'ai' && isAiBetaLocked) ? 'default' : value;

    if (bulkSettingsData) {
      const item = Array.isArray(bulkSettingsData)
        ? bulkSettingsData.find(i => i.slug === 'translation_type')
        : bulkSettingsData;
      const value = effective(item?.value || 'default');
      setBulkType(value);
      setOriginalBulkType(value);
      setAiConsent(value === 'ai');
    } else if (bulkSettingsDefaultData) {
      const defaultItem = Array.isArray(bulkSettingsDefaultData)
        ? bulkSettingsDefaultData.find(i => i.slug === 'translation_type')
        : null;
      const value = effective(defaultItem?.value || 'default');
      setBulkType(value);
      setOriginalBulkType(value);
      setAiConsent(value === 'ai');
    }
  }, [bulkSettingsData, bulkSettingsDefaultData, isAiBetaLocked]);

  useEffect(() => {
    if (deeplJustSavedRef.current) {
      deeplJustSavedRef.current = false;
      return;
    }
    if (deeplSettingsData) {
      const item = Array.isArray(deeplSettingsData)
        ? deeplSettingsData.find(i => i.slug === 'formality')
        : deeplSettingsData;
      const value = item?.value || 'default';
      setDeeplFormality(value);
      setOriginalDeeplFormality(value);
    } else if (deeplSettingsDefaultData) {
      const defaultItem = Array.isArray(deeplSettingsDefaultData)
        ? deeplSettingsDefaultData.find(i => i.slug === 'formality')
        : null;
      const value = defaultItem?.value || 'default';
      setDeeplFormality(value);
      setOriginalDeeplFormality(value);
    }
  }, [deeplSettingsData, deeplSettingsDefaultData]);

  useEffect(() => {
    if (configData) {
      const items = Array.isArray(configData) ? configData : [];
      if (!configInitializedRef.current || justSavedRef.current) {
        setModifiedConfigData(items);
        justSavedRef.current = false;
      }
      if (!configInitializedRef.current) {
        configInitializedRef.current = true;
      }
      setOriginalConfigData(items);
    } else if (defaultConfigData) {
      const items = Array.isArray(defaultConfigData) ? defaultConfigData : [];
      if (!configInitializedRef.current || justSavedRef.current) {
        setModifiedConfigData(items);
        justSavedRef.current = false;
      }
      setOriginalConfigData(items);
    }
  }, [configData, defaultConfigData]);

  useEffect(() => {
    const configChanged = JSON.stringify(modifiedConfigData) !== JSON.stringify(originalConfigData);
    const deeplChanged = deeplFormality !== originalDeeplFormality;
    const bulkChanged = bulkType !== originalBulkType;
    setHasChanges(configChanged || deeplChanged || bulkChanged);
  }, [modifiedConfigData, originalConfigData, deeplFormality, originalDeeplFormality, bulkType, originalBulkType]);

  const groupByNamespace = (items) => {
    return items.reduce((grouped, item) => {
      const namespace = item.namespace || 'other';
      if (!grouped[namespace]) grouped[namespace] = [];
      grouped[namespace].push(item);
      return grouped;
    }, {});
  };

  const groupedData = groupByNamespace(modifiedConfigData);
  const tabbedNamespaces = Object.keys(groupedData).filter(ns => groupedData[ns].length > 1);

  const defaultFormality = deeplSettingsDefaultData
    ? (Array.isArray(deeplSettingsDefaultData) ? deeplSettingsDefaultData.find(i => i.slug === 'formality') : null)?.value || 'default'
    : 'default';


  // Scrollspy: only tracks tabbedNamespaces (namespaces with >1 item)
  useEffect(() => {
    const grouped = groupByNamespace(modifiedConfigData);
    const nsList = Object.keys(grouped).filter(ns => grouped[ns].length > 1);
    namespacesRef.current = nsList;

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      const scrollContainer = document.getElementById('mainFrame') || window;
      const currentScrollY = scrollContainer === window ? window.scrollY : /** @type {HTMLElement} */ (scrollContainer).scrollTop;
      const scrollingDown = currentScrollY > lastScrollYRef.current;
      lastScrollYRef.current = currentScrollY;
      const threshold = scrollingDown ? window.innerHeight - 100 : 180;
      let activeIndex = 0;
      for (let i = 0; i < nsList.length; i++) {
        const el = sectionRefs.current[nsList[i]];
        if (el && el.getBoundingClientRect().top <= threshold) {
          activeIndex = i;
        }
      }
      setActiveTab(activeIndex);
    };

    const scrollContainer = document.getElementById('mainFrame') || window;
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [modifiedConfigData]);

  // Keep hoveredFieldRef in sync for scroll handler
  useEffect(() => {
    hoveredFieldRef.current = hoveredField;
  }, [hoveredField]);

  // Recalculate info box position after content changes (new height)
  useLayoutEffect(() => {
    if (!hoveredField) return;
    const boxHeight = infoBoxRef.current?.offsetHeight || 120;
    const colHeight = infoColumnRef.current?.offsetHeight || 500;
    const centeredTop = hoverCenterYRef.current - boxHeight / 2;
    const clampedTop = Math.max(0, Math.min(colHeight - boxHeight, centeredTop));
    setInfoTop(clampedTop);
  }, [hoveredField]);

  // Sticky simulation: update infoTop on scroll when not hovering
  useEffect(() => {
    const stickyOffset = 70;
    const handleStickyScroll = () => {
      if (hoveredFieldRef.current) return;
      const colRect = infoColumnRef.current?.getBoundingClientRect();
      if (!colRect) return;
      const boxHeight = infoBoxRef.current?.offsetHeight || 120;
      if (colRect.top >= stickyOffset) {
        setInfoTop(0);
      } else {
        const scrolledPast = stickyOffset - colRect.top;
        const maxTop = colRect.height - boxHeight;
        setInfoTop(Math.min(scrolledPast, Math.max(0, maxTop)));
      }
    };
    const scrollContainer = document.getElementById('mainFrame') || window;
    scrollContainer.addEventListener('scroll', handleStickyScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleStickyScroll);
  }, []);

  const getDefaultValue = (item) => {
    if (!Array.isArray(defaultConfigData)) return null;
    const defaultItem = defaultConfigData.find(d => d.slug === item.slug && d.namespace === item.namespace);
    return defaultItem ? defaultItem.value : null;
  };

  const handleFieldHover = (item, e) => {
    const switchEl = e.currentTarget.querySelector('.MuiSwitch-root');
    const targetRect = (switchEl || e.currentTarget).getBoundingClientRect();
    const centerY = targetRect.top + targetRect.height / 2;

    // Triangle position relative to infoColumn (position: relative)
    const colRect = infoColumnRef.current?.getBoundingClientRect();
    if (colRect) {
      setTriangleTop(centerY - colRect.top);
    }

    // Store center position for recalculation after render
    hoverCenterYRef.current = centerY - colRect.top;

    setHoveredField({
      name: fieldLabel(item.slug),
      defaultIncluded: getDefaultValue(item),
      namespace: formatSlug(item.namespace),
      slug: item.slug,
    });
  };

  const formatSlug = (slug) => {
    if (!slug) return '';
    return slug
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // merchant-facing names where the slug's own words are too technical; the slug itself is the
  // stored key and stays as it is
  const fieldLabel = (slug) => {
    if (slug === 'body_html') return t('settings.bulkConfiguration.fieldLabel_body_html', 'Description');
    if (slug === 'summary_html') return t('settings.bulkConfiguration.fieldLabel_summary_html', 'Summary');
    return formatSlug(slug);
  };

  const fieldDescriptionKey = {
    meta_title: 'settings.bulkConfiguration.fieldInfo_meta_title',
    meta_description: 'settings.bulkConfiguration.fieldInfo_meta_description',
    handle: 'settings.bulkConfiguration.fieldInfo_handle',
    product_type: 'settings.bulkConfiguration.fieldInfo_product_type',
  };

  const isModifiedFromDefault = (item) => {
    if (!Array.isArray(defaultConfigData)) return false;
    const defaultItem = defaultConfigData.find(d => d.slug === item.slug && d.namespace === item.namespace);
    if (!defaultItem) return false;
    return item.value !== defaultItem.value;
  };

  const handleSwitchToggle = (index) => {
    setModifiedConfigData(prevData => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], value: !newData[index].value };
      return newData;
    });
  };

  const handleResetItemToDefault = (itemIndex, item) => {
    const defaultItem = Array.isArray(defaultConfigData)
      ? defaultConfigData.find(d => d.slug === item.slug && d.namespace === item.namespace)
      : null;
    if (!defaultItem) return;
    const updatedData = [...modifiedConfigData];
    updatedData[itemIndex] = { ...updatedData[itemIndex], value: defaultItem.value };
    setModifiedConfigData(updatedData);
    dispatch(saveSettings(updatedData, () => {
      setOriginalConfigData(JSON.parse(JSON.stringify(updatedData)));
    }));
  };

  // Save is blocked as long as AI is being enabled without a confirmed consent; stored AI is confirmed.
  const aiConsentNeeded = bulkType === 'ai' && originalBulkType !== 'ai';
  const aiConsentMissing = aiConsentNeeded && !aiConsent;

  const handleBulkTypeChange = (value) => {
    setBulkType(value);
    // Switching away from an AI that is not stored yet revokes the confirmation — selecting AI again re-asks.
    if (value !== 'ai') setAiConsent(false);
    if (value === 'ai') scrollToAiConsentRef.current = true;
  };

  // The consent box only exists once AI is rendered, so the scroll has to wait for that
  // render. It sits below the fold on most screens and was easy to miss otherwise.
  useEffect(() => {
    if (bulkType !== 'ai' || !scrollToAiConsentRef.current) return;
    // the product limit banner renders once the count is in; it sits below the consent box and
    // is the thing to bring into view then, so the scroll waits for the count
    if (productsTotalPending) return;
    scrollToAiConsentRef.current = false;
    const target = aiProductLimitRef.current || aiConsentRef.current;
    if (!target) return;

    // Only scroll as far as needed to get the checkbox fully into view. The browser's own
    // smooth scroll has no duration control and felt like a jump, so it is animated by hand.
    const container = document.getElementById('mainFrame') || window;
    const isWindow = container === window;
    const viewportBottom = isWindow ? window.innerHeight : container.getBoundingClientRect().bottom;
    const overflow = target.getBoundingClientRect().bottom + AI_CONSENT_SCROLL_MARGIN - viewportBottom;
    if (overflow <= 0) return;

    const start = isWindow ? window.scrollY : container.scrollTop;
    const startTime = performance.now();
    const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);

    isProgrammaticScrollRef.current = true;
    clearTimeout(programmaticScrollTimerRef.current);
    programmaticScrollTimerRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, AI_CONSENT_SCROLL_DURATION + 200);

    let frame;
    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / AI_CONSENT_SCROLL_DURATION);
      const y = start + overflow * easeInOut(progress);
      if (isWindow) window.scrollTo(0, y); else container.scrollTop = y;
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [bulkType, productsTotalPending, showAiProductLimitNotice]);

  const handleSaveConfig = () => {
    if (aiConsentMissing) return;
    const configChanged = JSON.stringify(modifiedConfigData) !== JSON.stringify(originalConfigData);
    const deeplChanged = deeplFormality !== originalDeeplFormality;
    const bulkChanged = bulkType !== originalBulkType;

    if (configChanged) {
      justSavedRef.current = true;
      dispatch(saveSettings(modifiedConfigData, (response) => {
        console.log('Configuration saved:', response);
        setOriginalConfigData(JSON.parse(JSON.stringify(modifiedConfigData)));
      }));
    }

    if (deeplChanged) {
      deeplJustSavedRef.current = true;
      dispatch(saveDeeplSettings([
        { namespace: 'deepl', slug: 'formality', type: 'string', value: deeplFormality }
      ], (response) => {
        console.log('DeepL settings saved:', response);
        setOriginalDeeplFormality(deeplFormality);
        dispatch(getDeeplSettings());
      }));
    }

    if (bulkChanged) {
      bulkJustSavedRef.current = true;
      dispatch(saveBulkSettings([
        { namespace: 'bulk', slug: 'translation_type', type: 'string', value: bulkType }
      ], (response) => {
        if (response && response.success === false) {
          // Revert on rejection (e.g. AI requires the Growth plan).
          bulkJustSavedRef.current = false;
          setBulkType(originalBulkType);
          return;
        }
        setOriginalBulkType(bulkType);
        dispatch(getBulkSettings());
      }));
    }
  };

  const handleDiscardChanges = () => {
    setModifiedConfigData(JSON.parse(JSON.stringify(originalConfigData)));
    setDeeplFormality(originalDeeplFormality);
    setBulkType(originalBulkType);
    setAiConsent(originalBulkType === 'ai');
  };

  const handleResetDeeplToDefault = () => {
    dispatch(resetDeeplSettings());
    dispatch(getDeeplSettingsDefault((response) => {
      const data = response.data || response;
      const defaultItem = Array.isArray(data) ? data.find(i => i.slug === 'formality') : null;
      const value = defaultItem?.value || 'default';
      setDeeplFormality(value);
      deeplJustSavedRef.current = true;
      dispatch(saveDeeplSettings([
        { namespace: 'deepl', slug: 'formality', type: 'string', value }
      ], () => {
        setOriginalDeeplFormality(value);
        dispatch(getDeeplSettings());
      }));
    }));
  };

  const scrollToSection = (key) => {
    isProgrammaticScrollRef.current = true;
    clearTimeout(programmaticScrollTimerRef.current);
    programmaticScrollTimerRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 800);
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <React.Fragment>

      <Typography variant="h4" component="h2">
        {t('settings.bulkConfiguration.title', 'Bulk Configuration')}

        <SolutionsArticleLink
          article="machineTranslationSettings"
          subject={t('settings.bulkConfiguration.title', 'Bulk Configuration')}
          placement="heading"
        />
      </Typography>
      <Divider className="divider-white" />


      <Grid container spacing={3} alignItems="flex-start" className="page-content" sx={{ mt: 2 }}>
        {/* DeepL Settings — full width */}
        <Grid size={12}>
          <Paper sx={styles.paperFlat}>
            <Typography variant="h6" gutterBottom>
              {t('settings.bulkConfiguration.deeplTitle', 'DeepL & AI Settings')}
            </Typography>
            <Divider sx={styles.divider} />
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
              {t('settings.bulkConfiguration.deeplDescription', 'Applies to translations by DeepL and by AI. Translations via Google are not affected.')}
            </Typography>
            <Typography variant="body2" gutterBottom style={{ marginBottom: 8 }}>
              {t('settings.bulkConfiguration.deeplFormalityLabel', 'Formality')}
            </Typography>
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
              {t('settings.bulkConfiguration.deeplFormalityDescription', 'Controls the tone of translated text. Use "More" for a formal register (e.g. business communications), or "Prefer less" to fall back to an informal tone where supported.')}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
              <Typography variant="body2">
                <strong>{t('settings.bulkConfiguration.deeplFormalityCurrent', 'Current:')}</strong>{' '}
                {deeplFormality === 'prefer_less'
                  ? t('settings.bulkConfiguration.deeplFormalityPreferLess', 'Prefer less')
                  : t('settings.bulkConfiguration.deeplFormalityMore', 'More')}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="body2">
                  {t('settings.bulkConfiguration.deeplFormalityMore', 'More')}
                </Typography>
                <IOSSwitch
                  checked={deeplFormality === 'prefer_less'}
                  onChange={(e) => setDeeplFormality(e.target.checked ? 'prefer_less' : 'default')}
                />
                <Typography variant="body2">
                  {t('settings.bulkConfiguration.deeplFormalityPreferLess', 'Prefer less')}
                </Typography>
              </Box>
            </Box>
            {deeplSettingsDefaultData && deeplFormality !== defaultFormality && (
              <Box display="flex" justifyContent="flex-start" style={{ marginTop: 12 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetDeeplToDefault}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {t('system.resetToDefault', 'Reset to default')}
                </Button>
              </Box>
            )}
            {/* closed beta: shown only for shops where support has set something */}
            {(translationInstructions?.context || translationInstructions?.custom_instruction_all || Object.keys(translationInstructions?.custom_instructions || {}).length > 0) && (
              <Box style={{ marginTop: 24 }}>
                <Divider sx={styles.divider} />
                <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>
                  {t('settings.bulkConfiguration.translationInstructionsTitle', 'Store Context & Custom Instructions')}
                </Typography>
                <Box display="flex" alignItems="center" flexWrap="wrap" sx={[styles.aiNotice, { mt: 0, mb: 2, gap: 1.5 }]}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: '#f57c00' }} />
                  <Typography variant="body2" sx={[styles.aiNoticeText, { fontWeight: 600, flex: 1 }]}>
                    {t('settings.bulkConfiguration.translationInstructionsNotice', 'These settings are managed by langify support. To change them, please contact us.')}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => window.FreshworksWidget?.('open', 'ticketForm')}>
                    {t('settings.bulkConfiguration.translationInstructionsContact', 'Contact support')}
                  </Button>
                </Box>
                <Grid container spacing={2} alignItems="flex-start">
                  {translationInstructions.context && (
                    <React.Fragment>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <ReadOnlyHint
                          label={t('settings.bulkConfiguration.translationContextLabel', 'Store context – for all languages')}
                          value={translationInstructions.context}
                          max={translationInstructions.max_context_length}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('settings.bulkConfiguration.translationContextExplanation', 'What your store is about, so words with several meanings are translated correctly. Applies to all languages.')}
                        </Typography>
                      </Grid>
                    </React.Fragment>
                  )}
                  {(translationInstructions.custom_instruction_all || Object.keys(translationInstructions.custom_instructions || {}).length > 0) && (
                    <React.Fragment>
                      <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {translationInstructions.custom_instruction_all && (
                          <ReadOnlyHint
                            label={t('settings.bulkConfiguration.customInstructionLabelAll', 'Custom instruction – for all languages')}
                            value={translationInstructions.custom_instruction_all}
                            max={translationInstructions.max_custom_instruction_length}
                          />
                        )}
                        {Object.entries(translationInstructions.custom_instructions || {}).map(([locale, instruction]) => (
                          <ReadOnlyHint
                            key={locale}
                            label={t('settings.bulkConfiguration.customInstructionLabelFor', 'Custom instruction – {{language}}', {
                              language: (languages || []).find(l => l.code?.toLowerCase() === locale)?.name || locale,
                            })}
                            value={instruction}
                            max={translationInstructions.max_custom_instruction_length}
                          />
                        ))}
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('settings.bulkConfiguration.customInstructionExplanation', 'Rules for the translation, for example which terms to use or which names to keep.')}
                        </Typography>
                      </Grid>
                    </React.Fragment>
                  )}
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Bulk settings intro + Fields — plan-gated for free/basic */}
        <Grid size={12} sx={{ position: 'relative' }}>
          <Box sx={{
            opacity: isPlanRestricted ? 0.5 : 1,
            pointerEvents: isPlanRestricted ? 'none' : 'auto',
          }}>
            <Paper sx={[styles.paperFlat, { mb: 3 }]}>
              <Typography variant="h6" gutterBottom>
                {t('settings.bulkConfiguration.subtitle', 'Bulk Translation Settings')}
              </Typography>
              <Divider sx={styles.divider} />
              <Typography variant="body2" color="text.secondary">
                {t('settings.bulkConfiguration.fieldsDescription', 'Bulk translation allows you to machine-translate large amounts of content at once. Configure which fields in the different Shopify data-sections should be included or excluded in the process. Changes made here will apply to all future bulk translation jobs.')}
              </Typography>

              <Divider sx={styles.divider} />

              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                {t('settings.bulkConfiguration.bulkTypeTitle', 'Bulk Type')}
              </Typography>
              <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
                {t('settings.bulkConfiguration.bulkTypeDescription', 'Choose which translation engine is used for bulk translations. This setting only affects bulk jobs — single translations are unaffected.')}
              </Typography>

              <RadioGroup
                aria-label="bulk translation type"
                name="bulkType"
                value={bulkType}
                onChange={(e) => handleBulkTypeChange(e.target.value)}
              >
                <FormControlLabel
                  value="default"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {t('settings.bulkConfiguration.bulkTypeNmtLabel', 'Machine translation (NMT) — default')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('settings.bulkConfiguration.bulkTypeNmtDescription', 'Fast, high-quality translations powered by the classic machine-translation engines (DeepL / Google). Bulk jobs consume words from your available word balance.')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1.5, '& .MuiFormControlLabel-label': { mt: 0.5 } }}
                />
                {/*
                  Closed beta: the option itself is walled off (grey overlay, no pointer events,
                  radio disabled so the keyboard cannot reach it either). The notice below sits
                  outside the wall on purpose — its mail link has to stay clickable.
                */}
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{
                    opacity: showAiBetaWall ? 0.5 : 1,
                    pointerEvents: showAiBetaWall ? 'none' : 'auto',
                  }}>
                    <FormControlLabel
                      value="ai"
                      disabled={isAiBetaLocked}
                      control={<Radio />}
                      label={
                        <Box>
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <AutoAwesomeOutlinedIcon fontSize="small" color="primary" />
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {t('settings.bulkConfiguration.bulkTypeLlmLabel', 'AI translation (LLM)')}
                            </Typography>
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label={t('settings.bulkConfiguration.bulkTypeFreeBadge', 'Free from Growth')}
                            />
                            {/* The beta badge stays on the marker for beta shops too - it is
                                what tells them the engine is still under test. */}
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={<ScienceOutlinedIcon />}
                              label={t('system.beta', 'Beta')}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.bulkConfiguration.bulkTypeLlmDescription', 'Context-aware translations generated by an AI language model. It can take a little longer than NMT, but no words are deducted from your balance for bulk jobs — meaning you can use bulk translation free of charge (from the Growth plan upwards).')}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { mt: 0.5 } }}
                    />
                  </Box>
                  {showAiBetaWall && <div className="plan-restricted-overlay" />}
                </Box>
              </RadioGroup>

              {showAiBetaWall && (
                <Box sx={styles.aiBetaNotice}>
                  <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 1 }}>
                    <ScienceOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {t('settings.bulkConfiguration.aiBeta.title', 'AI translation is in a closed beta')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.bulkConfiguration.aiBeta.text', 'This feature is currently being tested with a limited number of shops and has to be enabled for your shop. Interested? Write to us and we will get back to you.')}
                    {' '}
                    <a href={`mailto:${AI_BETA_CONTACT_MAIL}`} style={{ fontWeight: 600 }}>
                      {AI_BETA_CONTACT_MAIL}
                    </a>
                  </Typography>
                </Box>
              )}

              {aiConsentNeeded && (
                <Box sx={styles.aiNotice}>
                  <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 1 }}>
                    <WarningAmberOutlinedIcon fontSize="small" sx={{ color: '#f57c00' }} />
                    <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {t('settings.bulkConfiguration.aiNotice.title', 'Before you enable AI translation')}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={[styles.aiNoticeText, { mb: 1 }]}>
                    {t('settings.bulkConfiguration.aiNotice.intro', 'AI translations are not deterministic, the same text can come out differently each time, so review them and watch for:')}
                  </Typography>

                  <Box component="ul" sx={styles.aiNoticeList}>
                    {AI_WEAKNESS_KEYS.map(({ key, text }) => (
                      <Box component="li" key={key} sx={{ mb: 0.25 }}>
                        <Typography variant="body2" sx={styles.aiNoticeText}>
                          {t(`settings.bulkConfiguration.aiNotice.${key}`, text)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Typography variant="body2" sx={[styles.aiNoticeText, { mb: 1.5 }]}>
                    {t('settings.bulkConfiguration.aiNotice.speedNote', 'AI translation is also notably slower than machine translation (NMT).')}
                  </Typography>

                  <Box display="flex" alignItems="flex-start" sx={{ gap: 1, mb: 1.5 }}>
                    <ShieldOutlinedIcon fontSize="small" color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                    <Typography variant="body2" sx={styles.aiNoticeText}>
                      {t('settings.bulkConfiguration.aiNotice.privacyText', 'For AI translation we use Scaleway SAS (Paris, France) as a sub-processor: processing inside the EU, data processing agreement pursuant to Art. 28 GDPR in place. Only the content to be translated is transmitted — no order, customer or payment data.')}
                      {' '}
                      <a href={AI_TRANSLATION_SOLUTION_URL} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                        {t('settings.bulkConfiguration.aiNotice.detailsLink', 'Details on quality and data protection')}
                      </a>
                    </Typography>
                  </Box>

                  <Box ref={aiConsentRef}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={aiConsent}
                          onChange={(e) => setAiConsent(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <Trans i18nKey="settings.bulkConfiguration.aiNotice.consentLabel">
                            <strong>I have read the notes above and agree</strong> that the content to be translated is transmitted to Scaleway SAS (France) for processing.
                          </Trans>
                        </Typography>
                      }
                      // the small checkbox icon and a body2 line are both 20px tall: no extra top
                      // padding, or the tick sits below the first line of the label
                      sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0 } }}
                    />
                    {aiConsentMissing && (
                      <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                        {t('settings.bulkConfiguration.aiNotice.consentRequired', 'This confirmation is required in order to enable the AI translation setting.')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {showAiProductLimitNotice && (
                <Box ref={aiProductLimitRef} sx={styles.aiNotice}>
                  <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 1 }}>
                    <InfoOutlinedIcon fontSize="small" sx={{ color: '#f57c00' }} />
                    <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {t('settings.bulkConfiguration.aiProductLimit.title', 'Note for stores with more than {{limit}} products', { limit: formatLargeNumber(AI_PRODUCT_LIMIT) })}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={styles.aiNoticeText}>
                    {t('settings.bulkConfiguration.aiProductLimit.text', 'Your store has {{count}} products. Once the closed beta ends, AI translation for stores with more than {{limit}} products will be available on the Premium plan only. Machine translation (NMT) stays available on your plan as before.', { count: formatLargeNumber(productsTotal), limit: formatLargeNumber(AI_PRODUCT_LIMIT) })}
                  </Typography>
                </Box>
              )}
            </Paper>
            <Box>
          {isDefaultPending && (
            <Typography variant="body2" color="text.secondary">
              {t('settings.bulkConfiguration.loadingConfig')}
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error">
              {t('settings.bulkConfiguration.errorLoadingConfig', { message: error.message })}
            </Typography>
          )}
          {/* Tabbed layout for namespaces with multiple items */}
          {tabbedNamespaces.length > 1 ? (
            <Box sx={styles.layout}>
              <Paper sx={styles.tabsSidebar}>
                <Tabs
                  orientation="vertical"
                  value={activeTab}
                  onChange={(_, newValue) => {
                    setActiveTab(newValue);
                    scrollToSection(tabbedNamespaces[newValue]);
                  }}
                  sx={styles.tabsRoot}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  {tabbedNamespaces.map((namespace) => (
                    <Tab key={namespace} label={formatSlug(namespace)} sx={styles.tab} />
                  ))}
                </Tabs>
                <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.bulkConfiguration.modifiedLegend', 'Modified from default')}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              <Box sx={styles.contentColumn} onMouseLeave={() => {
                setHoveredField(null);
                const colRect = infoColumnRef.current?.getBoundingClientRect();
                if (colRect) {
                  const boxHeight = infoBoxRef.current?.offsetHeight || 120;
                  const scrolledPast = 70 - colRect.top;
                  const maxTop = colRect.height - boxHeight;
                  setInfoTop(colRect.top >= 70 ? 0 : Math.min(scrolledPast, Math.max(0, maxTop)));
                } else {
                  setInfoTop(0);
                }
              }}>
                {tabbedNamespaces.map((namespace) => (
                  <Paper
                    key={namespace}
                    sx={[styles.paper, styles.namespaceSection]}
                    ref={el => {
                      sectionRefs.current[namespace] = el;
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={styles.namespaceTitle}>
                      {formatSlug(namespace)}
                    </Typography>
                    <List dense sx={styles.list}>
                      {groupedData[namespace].map((item) => {
                        const itemIndex = modifiedConfigData.findIndex(
                          (i) => i.slug === item.slug && i.namespace === item.namespace
                        );
                        return (
                          <ListItem
                            key={itemIndex}
                            dense
                            sx={styles.listItem}
                            onMouseEnter={(e) => handleFieldHover(item, e)}
                          >
                            <ListItemText primary={
                              <Box display="flex" alignItems="center">
                                {fieldLabel(item.slug)}
                                {(item.slug === 'handle' || item.slug === 'product_type') && (
                                  <ErrorOutlineIcon sx={styles.warningIcon} />
                                )}
                              </Box>
                            } />
                            <ListItemSecondaryAction>
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                {isModifiedFromDefault(item) && (
                                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main', flexShrink: 0 }} />
                                )}
                                <IOSSwitch
                                  checked={item.value || false}
                                  onChange={() => handleSwitchToggle(itemIndex)}
                                />
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Paper>
                ))}
              </Box>
              <Box sx={styles.infoColumn} ref={infoColumnRef}>
                {/* Triangle pointer — positioned in infoColumn (position: relative), independent of sticky box */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: -16,
                    top: triangleTop - 12,
                    width: 0,
                    height: 0,
                    borderTop: '12px solid transparent',
                    borderBottom: '12px solid transparent',
                    borderRight: '16px solid rgba(0,0,0,0.12)',
                    transition: 'top 0.15s ease',
                    opacity: hoveredField ? 1 : 0,
                    zIndex: 0,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: -14.5,
                    top: triangleTop - 11,
                    width: 0,
                    height: 0,
                    borderTop: '11px solid transparent',
                    borderBottom: '11px solid transparent',
                    borderRight: '15px solid #fff',
                    transition: 'top 0.15s ease',
                    opacity: hoveredField ? 1 : 0,
                    zIndex: 1,
                  }}
                />
                <Box
                  ref={infoBoxRef}
                  style={{
                    position: 'absolute',
                    top: infoTop,
                    left: 0,
                    right: 0,
                    transition: 'top 0.2s ease',
                  }}
                >
                  <Paper sx={styles.infoBox}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                      {t('settings.bulkConfiguration.fieldInfoTitle', 'Field information')}
                    </Typography>
                    <Divider style={{ marginBottom: 8 }} />
                    {hoveredField ? (
                      <>
                        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 4 }}>
                          <strong>{t('settings.bulkConfiguration.fieldName', 'Fieldname:')}</strong>{' '}
                          {hoveredField.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>{t('settings.bulkConfiguration.fieldDefault', 'Default:')}</strong>{' '}
                          {hoveredField.defaultIncluded
                            ? t('settings.bulkConfiguration.included', 'included')
                            : t('settings.bulkConfiguration.notIncluded', 'not-included')}
                        </Typography>
                        {fieldDescriptionKey[hoveredField.slug] && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t(fieldDescriptionKey[hoveredField.slug])}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('settings.bulkConfiguration.fieldInfoDescription', 'Enable or disable individual fields to control which content gets translated during bulk translation. Fields disabled here will be skipped in all future bulk translation jobs.')}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              </Box>
            </Box>
          ) : (
            tabbedNamespaces.map((namespace) => (
              <Paper key={namespace} sx={styles.paper}>
                <Typography variant="h6" gutterBottom sx={styles.namespaceTitle}>
                  {formatSlug(namespace)}
                </Typography>
                <Divider sx={styles.divider} />
                <List dense sx={styles.list}>
                  {groupedData[namespace].map((item) => {
                    const itemIndex = modifiedConfigData.findIndex(
                      (i) => i.slug === item.slug && i.namespace === item.namespace
                    );
                    return (
                      <ListItem key={itemIndex} dense sx={styles.listItem}>
                        <ListItemText primary={
                          <Box display="flex" alignItems="center">
                            {fieldLabel(item.slug)}
                            {(item.slug === 'handle' || item.slug === 'product_type') && (
                              <ErrorOutlineIcon sx={styles.warningIcon} />
                            )}
                          </Box>
                        } />
                        <ListItemSecondaryAction>
                          <IOSSwitch
                            checked={item.value || false}
                            onChange={() => handleSwitchToggle(itemIndex)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            ))
          )}
            </Box>
          </Box>
          {isPlanRestricted && (
            <React.Fragment>
              <div className="plan-restricted-overlay" />
              <UpgradePlanHint
                neededPlan="growth"
                position="center"
                placement="top"
                top={1}
                isOverlay
                features={[
                  t('settings.bulkConfiguration.upgradeFeature1', 'Fine-tune which fields get translated per Shopify resource.'),
                  t('settings.bulkConfiguration.upgradeFeature2', 'Available starting from the Growth plan.'),
                ]}
              />
            </React.Fragment>
          )}
        </Grid>
      </Grid>
      {hasChanges && (
        <Toolbar sx={[styles.actionBar, styles.highlight]} elevation={0}>
          <Typography component="h1" variant="h6" color="inherit" noWrap sx={styles.appBarLogo}>
            {/*
              Auf der dunklen Save-Bar (secondary.dark) erreicht das
              Indigo-Logo nur 1.70:1. Gegenstück wie in containers/app/index.js.
            */}
            <img className="logo-img" src={isDark ? "/langify-logo-white.svg" : "/langify-logo-accent.svg"} style={{width: 110, marginTop: -2}} alt={"logo"}/>
          </Typography>
          <Typography color="inherit" variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 400, '@media (max-width: 600px)': { fontSize: '0.85rem' } }}>
            {aiConsentMissing
              ? t('settings.bulkConfiguration.aiNotice.consentRequiredShort', 'Please confirm the AI translation notice')
              : t('itemsList.unsavedSection', 'Unsaved changes')}
          </Typography>
          <Box sx={styles.actions}>
            <Button
              aria-label="Discard"
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleDiscardChanges}
              disabled={isSavePending || isSaveDeeplPending || isSaveBulkPending}
              style={{marginRight: 8}}
            >
              {t('system.discard', 'Discard')}
            </Button>
            <Button
              aria-label="Save All"
              color="primary"
              variant="contained"
              size="small"
              onClick={handleSaveConfig}
              disabled={isSavePending || isSaveDeeplPending || isSaveBulkPending || aiConsentMissing}
            >
              {(isSavePending || isSaveDeeplPending || isSaveBulkPending)
                ? <><CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} />{t('system.saving', 'Saving...')}</>
                : <>{t('system.save', 'Save')}<SaveAltOutlinedIcon style={{ marginLeft: 5 }} /></>
              }
            </Button>
          </Box>
        </Toolbar>
      )}
    </React.Fragment>
  );
}

export default BulkConfiguration;
